/**
 * Shared date helpers for the schedule views (day/week/month).
 * Kept colocated with the schedule UI so the calendar internals don't leak
 * out to other features.
 */

export type ScheduleView = 'day' | 'week' | 'month';

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfWeek(date: Date): Date {
  const d = startOfDay(date);
  d.setDate(d.getDate() - d.getDay());
  return d;
}

export function endOfWeek(date: Date): Date {
  const start = startOfWeek(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

export function startOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfMonth(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * The full 6×7 grid covering a month — starts on the Sunday before/at the 1st
 * and runs through the Saturday after/at the last day. Always 42 cells.
 */
export function monthGridRange(date: Date): { start: Date; end: Date; days: Date[] } {
  const start = startOfWeek(startOfMonth(date));
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  const end = endOfDay(days[days.length - 1]);
  return { start, end, days };
}

/** Date range the fetch hook should query for a given view + anchor date. */
export function rangeForView(view: ScheduleView, anchor: Date): { start: Date; end: Date } {
  switch (view) {
    case 'day':
      return { start: startOfDay(anchor), end: endOfDay(anchor) };
    case 'week':
      return { start: startOfWeek(anchor), end: endOfWeek(anchor) };
    case 'month': {
      const { start, end } = monthGridRange(anchor);
      return { start, end };
    }
  }
}

/** Step the anchor by one period in the given direction. */
export function stepAnchor(view: ScheduleView, anchor: Date, dir: -1 | 1): Date {
  const d = new Date(anchor);
  switch (view) {
    case 'day':
      d.setDate(d.getDate() + dir);
      return d;
    case 'week':
      d.setDate(d.getDate() + 7 * dir);
      return d;
    case 'month':
      d.setMonth(d.getMonth() + dir);
      return d;
  }
}

/** Human-readable label for the current view + anchor. */
export function formatAnchorLabel(view: ScheduleView, anchor: Date): string {
  if (view === 'day') {
    return anchor.toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
  if (view === 'month') {
    return anchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
  }
  // Week
  const start = startOfWeek(anchor);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString([], opts)} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${start.toLocaleDateString([], opts)} – ${end.toLocaleDateString([], opts)}, ${end.getFullYear()}`;
}
